/**
 * PageParametrage — Global settings page under Administration.
 * Three sub-tabs: default NGAP/CCAM codes, ROSP alert thresholds, general prefs.
 * Sandbox-only (no backend persistence).
 */
import React from "react";
import { toast } from "sonner";
import { ClipboardList, FileText, Info, Plus, Settings, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, PageHeader } from "./shared";
import { ROSP_INDICATORS } from "./data";

export function PageParametrage() {
  const [tab, setTab] = React.useState("ngap");
  const SUB_TABS = [
    { id: "ngap", label: "Codes NGAP / CCAM par défaut", icon: FileText },
    { id: "rosp", label: "Seuils d'alerte ROSP", icon: ClipboardList },
    { id: "prefs", label: "Préférences générales", icon: Settings },
  ];

  const [ngapCodes, setNgapCodes] = React.useState([
    { code: "G", label: "Consultation généraliste", price: "25,00 €", active: true },
    { code: "TCG", label: "Téléconsultation généraliste", price: "25,00 €", active: true },
    { code: "CCX", label: "Consultation complexe", price: "46,00 €", active: true },
    { code: "APC", label: "Avis ponctuel de consultant", price: "55,00 €", active: false },
    { code: "V", label: "Visite à domicile", price: "35,00 €", active: false },
  ]);

  const [rospToggles, setRospToggles] = React.useState(
    ROSP_INDICATORS.reduce((acc, ind, i) => ({ ...acc, [ind.id]: i < 11 }), {})
  );

  const [prefs, setPrefs] = React.useState({
    dateFormat: "DD/MM/YYYY",
    notifEmail: true,
    notifSms: false,
    notifPush: true,
    theme: "clair",
  });

  return (
    <div data-testid="page-parametrage">
      <PageHeader title="Paramétrage" subtitle="Configuration transverse : facturation, indicateurs qualité, préférences utilisateur" />
      <div className="grid lg:grid-cols-[240px_1fr] gap-5">
        {/* Sub-nav */}
        <aside className="space-y-0.5">
          {SUB_TABS.map((t) => {
            const active = tab === t.id;
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`parametrage-tab-${t.id}`}
                className={`w-full text-left px-3 py-2 rounded text-[12.5px] transition-colors flex items-center gap-2 ${active ? "bg-slate-100 font-semibold text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </aside>

        {/* Content */}
        <div>
          {tab === "ngap" && (
            <Card className="p-5">
              <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1">Codes NGAP / CCAM par défaut</div>
              <div className="text-[12.5px] text-slate-500 mb-4">Ces codes seront proposés par défaut lors des envois PMS et facturation.</div>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-4 py-2 grid grid-cols-[0.6fr,2fr,1fr,0.7fr,0.5fr] gap-3 text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
                  <div>Code</div><div>Libellé</div><div>Tarif</div><div>Actif</div><div className="text-right"></div>
                </div>
                <div className="divide-y divide-slate-100">
                  {ngapCodes.map((c, i) => (
                    <div key={c.code} className="grid grid-cols-[0.6fr,2fr,1fr,0.7fr,0.5fr] gap-3 px-4 py-2.5 items-center text-[13px]" data-testid={`ngap-row-${c.code}`}>
                      <div className="font-mono font-semibold text-slate-900">{c.code}</div>
                      <div className="text-slate-700">{c.label}</div>
                      <div className="text-slate-700 tabular-nums">{c.price}</div>
                      <div>
                        <button
                          onClick={() => setNgapCodes(ngapCodes.map((x, j) => j === i ? { ...x, active: !x.active } : x))}
                          className={`relative w-9 h-5 rounded-full transition-colors ${c.active ? "" : "bg-slate-300"}`}
                          style={c.active ? { backgroundColor: "var(--bp)" } : {}}
                          data-testid={`ngap-toggle-${c.code}`}
                        >
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${c.active ? "left-4" : "left-0.5"}`} />
                        </button>
                      </div>
                      <div className="text-right">
                        <button
                          onClick={() => setNgapCodes(ngapCodes.filter((_, j) => j !== i))}
                          className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                          title="Supprimer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <button
                onClick={() => setNgapCodes([...ngapCodes, { code: "MSH", label: "Majoration sortie hospitalière", price: "30,00 €", active: false }])}
                className="mt-3 text-[12.5px] font-medium text-blue-700 hover:text-blue-900 inline-flex items-center gap-1.5"
                data-testid="ngap-add"
              >
                <Plus className="w-4 h-4" /> Ajouter un code
              </button>
            </Card>
          )}

          {tab === "rosp" && (
            <Card className="p-5">
              <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1">Seuils d&apos;alerte ROSP</div>
              <div className="text-[12.5px] text-slate-500 mb-4">Activez ou désactivez chaque indicateur qui alimentera le tableau <em>Suivi ROSP</em>.</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {ROSP_INDICATORS.map((ind) => (
                  <label key={ind.id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 hover:border-slate-300 cursor-pointer" data-testid={`rosp-toggle-${ind.id}`}>
                    <button
                      type="button"
                      onClick={() => setRospToggles({ ...rospToggles, [ind.id]: !rospToggles[ind.id] })}
                      className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${rospToggles[ind.id] ? "" : "bg-slate-300"}`}
                      style={rospToggles[ind.id] ? { backgroundColor: "var(--bp)" } : {}}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${rospToggles[ind.id] ? "left-4" : "left-0.5"}`} />
                    </button>
                    <span className={`text-[13px] ${rospToggles[ind.id] ? "text-slate-900 font-medium" : "text-slate-500"}`}>{ind.label}</span>
                  </label>
                ))}
              </div>
              <div className="mt-4 text-[11.5px] text-slate-500 flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5" /> {Object.values(rospToggles).filter(Boolean).length} / {ROSP_INDICATORS.length} indicateurs activés.
              </div>
            </Card>
          )}

          {tab === "prefs" && (
            <Card className="p-5 space-y-5">
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1">Format de date</div>
                <select
                  value={prefs.dateFormat}
                  onChange={(e) => setPrefs({ ...prefs, dateFormat: e.target.value })}
                  data-testid="prefs-dateformat"
                  className="w-full sm:w-64 border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900 bg-white"
                >
                  <option>DD/MM/YYYY</option>
                  <option>YYYY-MM-DD (ISO)</option>
                  <option>DD MMM YYYY</option>
                </select>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">Notifications</div>
                <div className="space-y-1.5">
                  {[
                    { k: "notifEmail", label: "Notifications par email" },
                    { k: "notifSms", label: "Notifications par SMS" },
                    { k: "notifPush", label: "Notifications push (navigateur)" },
                  ].map((n) => (
                    <label key={n.k} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 cursor-pointer">
                      <button
                        type="button"
                        onClick={() => setPrefs({ ...prefs, [n.k]: !prefs[n.k] })}
                        className={`relative w-9 h-5 rounded-full transition-colors ${prefs[n.k] ? "" : "bg-slate-300"}`}
                        style={prefs[n.k] ? { backgroundColor: "var(--bp)" } : {}}
                        data-testid={`prefs-toggle-${n.k}`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${prefs[n.k] ? "left-4" : "left-0.5"}`} />
                      </button>
                      <span className="text-[13px] text-slate-800">{n.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1">Thème</div>
                <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
                  {["clair", "sombre", "auto"].map((th) => (
                    <button
                      key={th}
                      onClick={() => setPrefs({ ...prefs, theme: th })}
                      data-testid={`prefs-theme-${th}`}
                      className={`px-3 py-1.5 text-[12.5px] font-medium rounded-md capitalize ${prefs.theme === th ? "text-white" : "text-slate-500 hover:bg-slate-50"}`}
                      style={prefs.theme === th ? { backgroundColor: "var(--bp)" } : {}}
                    >
                      {th}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-2">
                <Button className="text-white" style={{ backgroundColor: "var(--bp)" }} onClick={() => toast.success("Préférences enregistrées (sandbox).")}>
                  Enregistrer les préférences
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
